import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SquaresFour, Stack, Package, UsersThree, Brain, Globe } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import PageHeader from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";

function Stat({ Icon, label, value, mono }) {
  return (
    <div className="bg-card border border-border p-6 rounded-sm" data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between">
        <span className="overline text-muted-foreground">{label}</span>
        <Icon size={18} className="text-primary" weight="bold" />
      </div>
      <div className={`mt-4 text-3xl font-heading font-black tracking-tighter ${mono ? "font-mono text-xl" : ""}`}>
        {value}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { currentOrg } = useOrg();
  const [modules, setModules] = useState([]);
  const [counts, setCounts] = useState({ capabilities: 0, providers: 0, members: 0, kbs: 0 });

  useEffect(() => {
    if (!currentOrg) return;
    (async () => {
      const [mods, caps, provs, mem, kbs] = await Promise.all([
        api.get("/registry/modules"),
        api.get("/registry/capabilities"),
        api.get("/registry/providers"),
        api.get("/members"),
        api.get("/knowledge/bases"),
      ]);
      setModules(mods.data);
      setCounts({
        capabilities: caps.data.length,
        providers: provs.data.length,
        members: mem.data.length,
        kbs: kbs.data.length,
      });
    })().catch(() => {});
  }, [currentOrg]);

  const activeModules = modules.filter((m) => m.status === "active").length;

  return (
    <div>
      <PageHeader title={t("dashboard.title")} subtitle={t("dashboard.subtitle")} />

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        <Stat Icon={SquaresFour} label={t("dashboard.activeModules")} value={activeModules} />
        <Stat Icon={Stack} label={t("dashboard.capabilities")} value={counts.capabilities} />
        <Stat Icon={Package} label={t("dashboard.providers")} value={counts.providers} />
        <Stat Icon={UsersThree} label={t("dashboard.members")} value={counts.members} />
        <Stat Icon={Brain} label={t("dashboard.knowledgeBases")} value={counts.kbs} />
        <Stat Icon={Globe} label={t("dashboard.region")} value={currentOrg?.region || "—"} mono />
      </div>

      <div className="bg-card border border-border rounded-sm">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="font-heading font-bold tracking-tight">{t("dashboard.moduleMap")}</h3>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3">
          {modules.map((m) => (
            <div key={m.id} className="p-5 border-b border-r border-border" data-testid={`module-${m.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-muted-foreground">{m.id}</span>
                <Badge
                  variant={m.status === "active" ? "default" : "secondary"}
                  className="rounded-sm text-[10px] uppercase tracking-wider"
                >
                  {m.status}
                </Badge>
              </div>
              <div className="font-heading font-bold mt-2">{m.name}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {t("dashboard.milestone")}: <span className="font-mono">{m.milestone}</span>
              </div>
              {m.events_emitted?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {m.events_emitted.map((e) => (
                    <span key={e} className="font-mono text-[10px] px-1.5 py-0.5 bg-secondary rounded-sm">{e}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
